"use client";

import { useGlobalStore } from "@/app/context/globalStoreProvider";
import { useUI } from "@/app/context/UIProvider";
import { StyledInput } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StyledInputProps } from "@/components/ui/input/StyledInput";
import { SearchDates } from "@/generated/search.openapi";
import { GOOGLE_PLACES_MIN_CHARS, safeImportLibrary } from "@/lib/mapUtils";
import { recordClientMetric } from "@/lib/clientMetrics";
import { useDebouncedValue } from "@/hooks/client/useDebouncedValue";
import { useGooglePlacesSessionToken } from "@/hooks/client/useGooglePlacesSessionToken";
import { getValidRecentHotelStayDates } from "@/lib/hotel/recent-hotel-stay";
import { cn, getDateFormat } from "@/lib/utils";
import { X } from "lucide-react";
import { format } from "date-fns";
import { useOptionalFavoriteHotels } from "@/app/context/favoriteHotelsProvider";
import {
  buildFavoriteHotelDetailsUrl,
  buildFavoritesUrl,
  favoriteHotelStayAuthorityParams,
} from "@/lib/hotel/favorite-hotel-stay";
import type { FavoriteHotelStayAuthority } from "@/lib/hotel/favorite-hotel-stay";
import {
  captureFavoriteEvent,
  FAVORITE_OPEN,
} from "@/lib/analytics/hotelFavorites";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import React, {
  HTMLAttributes,
  KeyboardEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export type SearchSuggestion = {
  placeId: string;
  description: string;
  types: string[];
  structured_formatting: google.maps.places.StructuredFormatting;
  timeRangeString?: string;
  timeRange?: SearchDates;
  /** Session token to pass to PlacesService.getDetails() to close the billing session. */
  sessionToken?: google.maps.places.AutocompleteSessionToken;
  recentHotel?: {
    propertyId: number;
    checkin: string;
    checkout: string;
    numberOfAdults: number;
    childrenAges: number[];
    searchSessionId?: string;
  };
  favoriteHotel?: {
    propertyId: number;
  };
};

export type SupplementalSuggestion = {
  key: string;
  description: string;
  secondaryText?: string;
  group?: string;
  onSelect: () => void;
};
export type SupplementalSuggestionsLoader = (request: {
  query: string;
  predictions: SearchSuggestion[];
  signal: AbortSignal;
}) => Promise<SupplementalSuggestion[]>;

type GoogleSearchOptions = Omit<
  google.maps.places.AutocompletionRequest,
  "input"
>;

type Props = {
  googleSearchOptions?: GoogleSearchOptions;
  onSuggestionClick?: (suggestion: SearchSuggestion | null) => void;
  innerContainerClassName?: HTMLAttributes<HTMLDivElement>["className"];
  suggestionBoxWidth?: number;
  suggestionBoxLeftOffset?: number;
  suggestionBoxTopOffset?: number;
  isSuggestionBgWhite?: boolean;
  showRecentSearches?: boolean;
  /** When provided, input is synced to this when not focused (e.g. "" to show placeholder, or submitted location) */
  displayValue?: string;
  /** When provided, changing this re-applies displayValue (e.g. view so list/map switch clears input) */
  displayValueSyncKey?: string;
  /** When provided, shows a clear (X) button when the input has a value */
  onClear?: () => void;
  /**
   * Marks the input as part of a rental car search.
   * - When `googleSearchOptions.types` is also provided, those types win (caller intent).
   * - When no `googleSearchOptions.types` is provided, defaults to airport-only suggestions.
   * - Also disables the supplementary autocomplete request for non-explicit car searches.
   */
  isCarSearch?: boolean;
  /** Optional domain-owned choices, shown after normal location predictions. */
  loadSupplementalSuggestions?: SupplementalSuggestionsLoader;
  seedSupplementalSuggestions?: SupplementalSuggestion[];
  /**
   * Live form stay values used by favorite links before the form is submitted.
   * Dates are optional so flexible/cleared forms preserve live occupancy while
   * excluding stale concrete dates. `undefined` lets legacy callers fall back
   * to the URL because they have no live form authority.
   */
  activeStay?: FavoriteHotelStayAuthority;
} & StyledInputProps;

interface Prediction {
  description: string;
  place_id: string;
  types: string[];
  structured_formatting: google.maps.places.StructuredFormatting;
}

interface PredictionResult {
  predictions: Prediction[] | null;
  status: string;
}

const defaultSuggestions = { hotels: [], places: [] };

// Coalesces rapid keystrokes into one autocomplete request. Mobile uses 300 ms;
// desktop typing is faster so we go shorter.
const AUTOCOMPLETE_DEBOUNCE_MS = 150;

export const buildRecentHotelDetailsUrl = (
  recent: NonNullable<SearchSuggestion["recentHotel"]>
): string => {
  const { checkin, checkout } = getValidRecentHotelStayDates(recent);
  const params = new URLSearchParams({
    checkin,
    checkout,
    numberOfAdults: String(recent.numberOfAdults),
    childrenAges: recent.childrenAges.join(","),
  });
  if (recent.searchSessionId) {
    params.set("sid", recent.searchSessionId);
  }
  return `/hotel/details/${recent.propertyId}?${params.toString()}`;
};

const SearchInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      label,
      containerClassName,
      innerContainerClassName,
      className,
      iconSrc,
      iconComponent,
      googleSearchOptions,
      onSuggestionClick: _onSuggestionClick,
      suggestionBoxWidth,
      suggestionBoxLeftOffset,
      suggestionBoxTopOffset,
      isSuggestionBgWhite,
      showRecentSearches = true,
      displayValue,
      displayValueSyncKey,
      onClear,
      isCarSearch = false,
      loadSupplementalSuggestions,
      seedSupplementalSuggestions,
      activeStay,
      onFocus: onInputFocus,
      ...otherProps
    },
    ref
  ) => {
    const listId = useId();
    const [supplemental, setSupplemental] = useState<SupplementalSuggestion[]>(
      []
    );
    const [activeSupplementalKey, setActiveSupplementalKey] =
      useState<string>();
    const supplementalAbort = useRef<AbortController | null>(null);
    const seedSupplementalRef = useRef(seedSupplementalSuggestions);
    seedSupplementalRef.current = seedSupplementalSuggestions;
    const supplementalLoaderRef = useRef(loadSupplementalSuggestions);
    supplementalLoaderRef.current = loadSupplementalSuggestions;
    const invalidateSupplemental = () => {
      supplementalAbort.current?.abort();
      setSupplemental([]);
      setActiveSupplementalKey(undefined);
    };
    const loadSupplemental = async (
      query: string,
      predictions: SearchSuggestion[]
    ) => {
      supplementalAbort.current?.abort();
      const controller = new AbortController();
      supplementalAbort.current = controller;
      const loader = supplementalLoaderRef.current;
      if (!loader) return;
      try {
        const rows = await loader({
          query,
          predictions,
          signal: controller.signal,
        });
        if (
          !controller.signal.aborted &&
          supplementalLoaderRef.current === loader
        ) {
          setSupplemental([
            ...(query ? [] : seedSupplementalRef.current ?? []),
            ...rows,
          ]);
        }
      } catch {
        // Optional choices never prevent selecting a normal location.
      }
    };
    useEffect(() => {
      if (!loadSupplementalSuggestions) invalidateSupplemental();
      return () => supplementalAbort.current?.abort();
    }, [loadSupplementalSuggestions]);
    const [recentSearches, setRecentSearches] = useState<SearchSuggestion[]>(
      []
    );
    const [suggestions, setSuggestions] = useState<{
      hotels: SearchSuggestion[];
      places: SearchSuggestion[];
    }>(defaultSuggestions);
    const { searchData } = useGlobalStore();
    const favorites = useOptionalFavoriteHotels();
    const posthog = usePostHog();
    const favoriteHotelSuggestions = useMemo<SearchSuggestion[]>(
      () =>
        (favorites?.history.items ?? []).map((hotel) => ({
          placeId: `favorite-hotel-${hotel.propertyId}`,
          description: hotel.propertyName,
          types: ["lodging"],
          structured_formatting: {
            main_text: hotel.propertyName,
            secondary_text: hotel.propertyAddress ?? "",
            main_text_matched_substrings: [],
          },
          favoriteHotel: { propertyId: hotel.propertyId },
        })),
      [favorites?.history.items]
    );
    const favoriteStayParams = activeStay
      ? favoriteHotelStayAuthorityParams(activeStay)
      : new URLSearchParams(
          typeof window === "undefined" ? "" : window.location.search
        );
    const favoritesHref = activeStay
      ? `/favorites?${favoriteStayParams.toString()}`
      : typeof window === "undefined"
        ? "/favorites"
        : buildFavoritesUrl(favoriteStayParams);
    const recentHotelSuggestions = useMemo<SearchSuggestion[]>(
      () =>
        (searchData.recentHotels ?? []).map((hotel) => ({
          placeId: `recent-hotel-${hotel.propertyId}`,
          description: hotel.propertyName,
          types: ["lodging"],
          structured_formatting: {
            main_text: hotel.propertyName,
            secondary_text: hotel.propertyAddress ?? "",
            main_text_matched_substrings: [],
          },
          timeRangeString: `${format(getDateFormat(hotel.checkin), "MMM d")} - ${format(
            getDateFormat(hotel.checkout),
            "MMM d"
          )}`,
          recentHotel: {
            propertyId: hotel.propertyId,
            checkin: hotel.checkin,
            checkout: hotel.checkout,
            numberOfAdults: hotel.numberOfAdults,
            childrenAges: hotel.childrenAges,
            searchSessionId: hotel.searchSessionId,
          },
        })),
      [searchData.recentHotels]
    );
    const recentActivity = useMemo(
      () => [
        ...favoriteHotelSuggestions,
        ...recentHotelSuggestions,
        ...recentSearches,
      ],
      [favoriteHotelSuggestions, recentHotelSuggestions, recentSearches]
    );
    const [placeAutocompleteService, setPlaceAutocompleteService] =
      React.useState<google.maps.places.AutocompleteService>();
    const [activeSuggestion, setActiveSuggestion] =
      useState<SearchSuggestion>();
    const [inputFocus, setInputFocus] = useState(false);
    const [hasValue, setHasValue] = useState(
      () => !!(displayValue || otherProps.defaultValue)
    );
    const [inputValue, setInputValue] = useState<string>(
      () => displayValue ?? String(otherProps.defaultValue ?? "")
    );
    // Autocomplete query — only set by the onChange handler (real user typing).
    // Programmatic value commits (initial mount with prefilled value, displayValue
    // prop sync, suggestion select, blur restore, clear) leave this empty so they
    // don't trigger Places API calls.
    const [autocompleteQuery, setAutocompleteQuery] = useState("");
    const debouncedAutocompleteQuery = useDebouncedValue(
      autocompleteQuery,
      AUTOCOMPLETE_DEBOUNCE_MS
    );
    // Discards autocomplete responses that arrive after a newer keystroke or
    // after the query is reset (suggestion picked, input cleared, fell below
    // threshold) — keeps stale predictions from repopulating the dropdown.
    const requestIdRef = useRef(0);
    // Mirrors googleSearchOptions so the autocomplete effect can read the
    // latest options without listing the prop in its dep array. Otherwise an
    // unmemoized inline object from the caller would trigger a fresh Places
    // fetch on every parent re-render — defeating the debounce coalescing.
    const googleSearchOptionsRef = useRef(googleSearchOptions);
    useEffect(() => {
      googleSearchOptionsRef.current = googleSearchOptions;
    });
    // Holds the pending blur grace-window timer so onFocus can cancel it.
    // Without this, a quick blur+refocus inside the 250 ms window still
    // wipes the in-progress query and resets the session token.
    const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const cancelBlurTimer = () => {
      if (blurTimerRef.current !== null) {
        clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
    };
    const tempRef = useRef<HTMLInputElement>(null);
    const inputRef =
      (ref as React.RefObject<HTMLInputElement> | undefined) ?? tempRef;
    const containerRef = useRef<HTMLDivElement>(null);
    const { createToken, getToken, consumeToken, resetToken } =
      useGooglePlacesSessionToken();

    // When displayValue is provided: sync input when not focused (e.g. "" = show placeholder).
    // Initial value is set via defaultValue below so the input mounts empty; this handles updates.
    // inputRef omitted from deps: refs have stable identity and don't trigger re-runs.
    useLayoutEffect(() => {
      if (displayValue !== undefined && inputRef?.current && !inputFocus) {
        inputRef.current.value = displayValue;
        setInputValue(displayValue);
      }
      setHasValue(!!displayValue);
    }, [displayValue, inputFocus, displayValueSyncKey]); // eslint-disable-line react-hooks/exhaustive-deps -- inputRef is stable

    useEffect(() => {
      const recentSearches = searchData?.recentSearchSessions ?? [];
      setRecentSearches(
        recentSearches.length
          ? recentSearches
              .filter(
                (item, index, self) =>
                  index ===
                  self.findIndex(
                    (t) =>
                      t.promptResponse?.request.mainQuery ===
                      item.promptResponse?.request.mainQuery
                  )
              )
              .map((item) => ({
                description: item.promptResponse?.request.mainQuery as string,
                placeId: item.promptResponse?.request.placeId as string,
                types: item.promptResponse?.request.types ?? [],
                structured_formatting: {
                  main_text: item.promptResponse?.request.mainQuery as string,
                  secondary_text: item.promptResponse?.request
                    .secondaryQuery as string,
                  main_text_matched_substrings: [],
                },
                timeRangeString: item.request?.hotels?.searchDates
                  ? getTimeRangeString(item.request?.hotels.searchDates)
                  : undefined,
                timeRange: item.request?.hotels?.searchDates,
              }))
              .filter((item) => item.placeId)
          : []
      );
    }, [searchData?.recentSearchSessions]);

    const getTimeRangeString = (timeRangeString: SearchDates) => {
      const checkin = getDateFormat(
        timeRangeString.checkinStr
          ? timeRangeString.checkinStr
          : timeRangeString.checkin
      );
      const checkout = getDateFormat(
        timeRangeString.checkoutStr
          ? timeRangeString.checkoutStr
          : timeRangeString.checkout
      );
      return `${format(checkin, "MMM d")} - ${format(checkout, "MMM d")}`;
    };

    useEffect(() => {
      (async () => {
        try {
          if (!window.google?.maps.places) {
            await safeImportLibrary("places");
          }
          setPlaceAutocompleteService(
            new window.google.maps.places.AutocompleteService()
          );
        } catch {
          // Places API failed to load; component will handle degraded state
        }
      })();
    }, []);

    // Caller-supplied `googleSearchOptions.types` always wins. The `isCarSearch`
    // airport-only shortcut is only applied when the caller hasn't specified
    // their own types — otherwise the rental-car flow (which passes
    // VEHICLE_SEARCH_OPTIONS = airport + locality + transit + POI) would be
    // silently narrowed back to airports, dropping the non-airport pickup
    // suggestions (city / hotel / station) the vehicle flow relies on.
    const baseTypes =
      googleSearchOptions?.types ??
      (isCarSearch
        ? ["airport"]
        : ["airport", "lodging", "locality", "neighborhood", "street_address"]);

    // Capped at 5 to stay within one API request. administrative_area_level_2 (county)
    // and political (broad catch-all) were intentionally dropped — low search value,
    // high overlap with locality/administrative_area_level_1/country.
    const supplementaryTypes = [
      "point_of_interest",
      "administrative_area_level_1",
      "country",
      "train_station",
      "transit_station",
    ];

    const deduplicateByPlaceId = (predictions: Prediction[]): Prediction[] => {
      const seen = new Set<string>();
      return predictions.filter((prediction) => {
        if (seen.has(prediction.place_id)) {
          return false;
        }
        seen.add(prediction.place_id);
        return true;
      });
    };
    const makeMultipleRequestsParallel = (
      value: string,
      googleSearchOptions?: GoogleSearchOptions
    ) => {
      const sessionToken = getToken();
      const requests: Promise<PredictionResult>[] = [
        // Main request: airports, hotels, cities, neighborhoods, addresses
        new Promise<PredictionResult>((resolve) => {
          recordClientMetric(
            "google_places_autocomplete_requested",
            "hotel_search"
          );
          placeAutocompleteService?.getPlacePredictions(
            {
              input: value,
              ...googleSearchOptions,
              types: baseTypes,
              sessionToken,
            },
            (
              predictions: Prediction[] | null,
              status: google.maps.places.PlacesServiceStatus
            ) => {
              recordClientMetric(
                status === "OK"
                  ? "google_places_autocomplete_succeeded"
                  : "google_places_autocomplete_failed",
                "hotel_search"
              );
              resolve({ predictions: predictions || [], status });
            }
          );
        }),
      ];

      if (!isCarSearch) {
        // Supplementary request: POIs, regions, countries, transit
        requests.push(
          new Promise<PredictionResult>((resolve) => {
            recordClientMetric(
              "google_places_autocomplete_requested",
              "hotel_search"
            );
            placeAutocompleteService?.getPlacePredictions(
              {
                input: value,
                ...googleSearchOptions,
                types: supplementaryTypes,
              },
              (
                predictions: Prediction[] | null,
                status: google.maps.places.PlacesServiceStatus
              ) => {
                recordClientMetric(
                  status === "OK"
                    ? "google_places_autocomplete_succeeded"
                    : "google_places_autocomplete_failed",
                  "hotel_search"
                );
                resolve({ predictions: predictions || [], status });
              }
            );
          })
        );
      }

      return Promise.all(requests).then((results: PredictionResult[]) => {
        const allPredictions: Prediction[] = results
          .filter((result) => result.status === "OK")
          .flatMap((result) => result.predictions || []);

        if (allPredictions.length > 0) {
          return {
            predictions: deduplicateByPlaceId(allPredictions),
            status: "OK",
          };
        } else {
          return {
            predictions: null,
            status: results.some((r) => r.status === "OK")
              ? "ZERO_RESULTS"
              : "ERROR",
          };
        }
      });
    };
    const formatPredictions = (
      predictions: Prediction[]
    ): SearchSuggestion[] => {
      if (!predictions) return [];

      const cityStateNames = [
        "hong kong",
        "singapore",
        "macau",
        "macao",
        "monaco",
        "vatican city",
        "san marino",
        "andorra",
        "liechtenstein",
        "gibraltar",
        "malta",
        "maldives",
        "hawaii",
        "greenland",
        "jersey",
        "guernsey",
        "isle of man",
        "faroe islands",
      ];
      const filteredPredictions = predictions.filter((prediction) => {
        const descriptionLower = prediction.description.toLowerCase();
        const isCityState = cityStateNames.some((name) =>
          descriptionLower.includes(name)
        );

        // Keep if it's a city-state OR if it has your original types
        const hasOriginalTypes = prediction.types.some(
          (type) =>
            baseTypes.includes(type) || supplementaryTypes.includes(type)
        );

        return isCityState || hasOriginalTypes;
      });
      // Remove city versions when country version exists
      const final = filteredPredictions.filter((prediction) => {
        const isCityState = cityStateNames.some((name) =>
          prediction.description.toLowerCase().includes(name)
        );

        if (!isCityState) return true;

        const hasCountryVersion = filteredPredictions.some(
          (p) =>
            p.description === prediction.description &&
            p.types.includes("country")
        );

        return !hasCountryVersion || prediction.types.includes("country");
      });

      return final.map((suggestion) => ({
        description: suggestion.description,
        placeId: suggestion.place_id,
        types: suggestion.types,
        structured_formatting: suggestion.structured_formatting,
      }));
    };

    useEffect(() => {
      if (!placeAutocompleteService) {
        return;
      }
      if (
        !debouncedAutocompleteQuery ||
        debouncedAutocompleteQuery.length < GOOGLE_PLACES_MIN_CHARS.location
      ) {
        // Bump requestId so any in-flight response from the previous query
        // can't repopulate suggestions after we've cleared them.
        requestIdRef.current++;
        setSuggestions(defaultSuggestions);
        return;
      }
      const myRequestId = ++requestIdRef.current;
      (async () => {
        try {
          const { predictions, status }: PredictionResult =
            await makeMultipleRequestsParallel(
              debouncedAutocompleteQuery,
              googleSearchOptionsRef.current
            );
          if (myRequestId !== requestIdRef.current) return;
          if (status === "OK" && predictions) {
            const _hotelsSuggestions: Prediction[] = predictions.filter(
              (prediction) => prediction.types.includes("lodging")
            );
            const _placesSuggestions: Prediction[] = predictions.filter(
              (prediction) => !prediction.types.includes("lodging")
            );

            const hotelsSuggestions: SearchSuggestion[] =
              formatPredictions(_hotelsSuggestions);
            const placesSuggestions: SearchSuggestion[] =
              formatPredictions(_placesSuggestions);

            void loadSupplemental(
              debouncedAutocompleteQuery,
              placesSuggestions
            );
            setSuggestions({
              hotels: hotelsSuggestions,
              places: placesSuggestions,
            });
            setActiveSuggestion(
              recentActivity[0] ??
                placesSuggestions?.[0] ??
                hotelsSuggestions?.[0] ??
                undefined
            );
          } else {
            setSuggestions(defaultSuggestions);
          }
        } catch {
          if (myRequestId !== requestIdRef.current) return;
          setSuggestions(defaultSuggestions);
        }
      })();
      // googleSearchOptions is read from a ref to avoid re-fetching on every
      // parent re-render when the caller passes an unmemoized object.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedAutocompleteQuery, placeAutocompleteService]);

    const showSuggestions = useMemo(
      () =>
        Boolean(
          supplemental.length ||
            suggestions.hotels.length ||
            suggestions.places.length ||
            (showRecentSearches &&
              favorites?.enabled &&
              inputFocus &&
              !inputValue &&
              (favorites.history.status === "loading" ||
                favorites.history.status === "error" ||
                favorites.history.nextCursor !== null)) ||
            (showRecentSearches &&
              recentActivity.length &&
              inputFocus &&
              !inputValue)
        ),
      [
        supplemental.length,
        suggestions.hotels.length,
        suggestions.places.length,
        showRecentSearches,
        favorites?.enabled,
        favorites?.history.status,
        favorites?.history.nextCursor,
        recentActivity.length,
        inputFocus,
        inputValue,
      ]
    );

    const onSuggestionClick = (suggestion: SearchSuggestion) => {
      cancelBlurTimer();
      invalidateSupplemental();
      setInputFocus(false);
      if (suggestion.favoriteHotel) {
        const { propertyId } = suggestion.favoriteHotel;
        captureFavoriteEvent(
          posthog,
          FAVORITE_OPEN,
          propertyId,
          "favorites_history"
        );
        window.location.assign(
          buildFavoriteHotelDetailsUrl(propertyId, favoriteStayParams)
        );
        return;
      }
      if (suggestion.recentHotel) {
        window.location.assign(
          buildRecentHotelDetailsUrl(suggestion.recentHotel)
        );
        return;
      }
      const sessionToken = consumeToken();
      setSuggestions(defaultSuggestions);
      _onSuggestionClick?.({ ...suggestion, sessionToken });
      setHasValue(true);
      if (inputRef?.current) {
        inputRef.current.value = suggestion.description;
        setInputValue(suggestion.description);
        // Reset the query and invalidate any in-flight request — selecting a
        // suggestion ends this autocomplete session, no follow-up fetch.
        setAutocompleteQuery("");
        requestIdRef.current++;
        setTimeout(() => {
          inputRef.current?.blur();
        }, 50);
      }
    };

    // For car search, treat hotels as empty so keyboard nav and auto-select skip them
    const effectiveHotels = isCarSearch ? [] : suggestions.hotels;

    const googleOptions =
      showRecentSearches && !inputValue && recentActivity.length
        ? recentActivity
        : [...effectiveHotels, ...suggestions.places];
    const selectSupplemental = (row: SupplementalSuggestion) => {
      // Validate before aborting: selection ends discovery and the blur grace window.
      if (!supplementalLoaderRef.current || !supplemental.includes(row)) return;
      cancelBlurTimer();
      row.onSelect();
      invalidateSupplemental();
      requestIdRef.current++;
      setSuggestions(defaultSuggestions);
      setActiveSuggestion(undefined);
      setInputFocus(false);
      setAutocompleteQuery("");
      setInputValue(row.description);
      setHasValue(true);
      resetToken();
      if (inputRef.current) inputRef.current.value = row.description;
      inputRef.current?.blur();
      cancelBlurTimer();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        cancelBlurTimer();
        invalidateSupplemental();
        requestIdRef.current++;
        setSuggestions(defaultSuggestions);
        setActiveSuggestion(undefined);
        setInputFocus(false);
        setAutocompleteQuery("");
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const count = googleOptions.length + supplemental.length;
        if (!count) return;
        const currentIndex = activeSupplementalKey
          ? googleOptions.length +
            supplemental.findIndex((row) => row.key === activeSupplementalKey)
          : googleOptions.findIndex(
              (row) => row.placeId === activeSuggestion?.placeId
            );
        const next =
          event.key === "ArrowDown"
            ? (currentIndex + 1) % count
            : currentIndex < 0
              ? count - 1
              : (currentIndex - 1 + count) % count;
        if (next < googleOptions.length) {
          setActiveSuggestion(googleOptions[next]);
          setActiveSupplementalKey(undefined);
        } else {
          setActiveSuggestion(undefined);
          setActiveSupplementalKey(
            supplemental[next - googleOptions.length].key
          );
        }
      } else if (event.key === "Enter") {
        event.preventDefault();
        const row = supplemental.find(
          (row) => row.key === activeSupplementalKey
        );
        if (row) selectSupplemental(row);
        else if (activeSuggestion && googleOptions.includes(activeSuggestion))
          onSuggestionClick(activeSuggestion);
      }
    };

    const { isLessThanLg } = useUI();

    const labelRef = useRef<HTMLDivElement>(null);

    return (
      <div className={cn("relative", containerClassName)} ref={containerRef}>
        {label && (
          <div
            ref={labelRef}
            className="mb-1 text-xs font-semibold text-muted-foreground"
          >
            {label}
          </div>
        )}
        <StyledInput
          containerClassName={cn("h-14", innerContainerClassName)}
          className={cn("text-sm", className)}
          iconSrc={iconSrc}
          iconComponent={iconComponent}
          ref={inputRef}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showSuggestions && inputFocus}
          aria-controls={listId}
          aria-activedescendant={
            inputFocus
              ? activeSupplementalKey
                ? `${listId}-extra-${activeSupplementalKey}`
                : activeSuggestion
                  ? `${listId}-place-${activeSuggestion.placeId}`
                  : undefined
              : undefined
          }
          {...(displayValue !== undefined && { defaultValue: displayValue })}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            requestIdRef.current++;
            invalidateSupplemental();
            setActiveSuggestion(undefined);
            setSuggestions(defaultSuggestions);
            _onSuggestionClick?.(null);
            setHasValue(!!e.target.value);
            setInputValue(e.target.value);
            // onChange is the only place that feeds the autocomplete query —
            // all programmatic value commits leave it alone.
            setAutocompleteQuery(e.target.value);
            if (!inputFocus && e.target.value) {
              setInputFocus(true);
            }
          }}
          onBlur={() => {
            supplementalAbort.current?.abort();
            // Synchronously invalidate any in-flight Places response. If the
            // bump is deferred into the setTimeout, a response landing inside
            // the 250 ms window passes the requestId check and repopulates
            // stale suggestions.
            requestIdRef.current++;
            cancelBlurTimer();
            blurTimerRef.current = setTimeout(() => {
              blurTimerRef.current = null;
              if (inputRef?.current) {
                if (displayValue !== undefined) {
                  inputRef.current.value = displayValue;
                  setInputValue(displayValue);
                  setHasValue(!!displayValue);
                  resetToken();
                } else {
                  // suggestions reflect debouncedAutocompleteQuery. If the
                  // user typed faster than the debounce settles and blurred
                  // inside the window, the dropdown still shows results for
                  // the previous query — auto-selecting one would commit a
                  // value that doesn't match what they typed.
                  const suggestionsAreFresh =
                    inputRef.current.value === debouncedAutocompleteQuery;
                  const sug = suggestionsAreFresh
                    ? !isCarSearch && suggestions.hotels.length
                      ? suggestions.hotels[0]
                      : suggestions.places.length
                        ? suggestions.places[0]
                        : undefined
                    : undefined;
                  if (sug) {
                    const sessionToken = consumeToken();
                    _onSuggestionClick?.({ ...sug, sessionToken });
                    inputRef.current.value = sug.description;
                    setInputValue(sug.description);
                    setHasValue(true);
                  } else {
                    resetToken();
                  }
                }
                // Reset the query after the grace window — synchronous reset
                // would propagate through the debounce → autocomplete effect
                // and collapse `suggestions` ~150 ms in, before slow clicks
                // on the dropdown have had a chance to register.
                setAutocompleteQuery("");
              }
              invalidateSupplemental();
              setInputFocus(false);
              setActiveSuggestion(undefined);
            }, 250);
          }}
          onFocus={(event) => {
            // Cancel any pending blur timer — refocusing inside the grace
            // window means the user is resuming, not abandoning.
            cancelBlurTimer();
            setInputFocus(true);
            createToken();
            if (!autocompleteQuery) {
              setSupplemental(seedSupplementalRef.current ?? []);
              void loadSupplemental("", []);
            }
            onInputFocus?.(event);
          }}
          suggestionsComponent={
            showSuggestions &&
            inputFocus && (
              <div
                id={listId}
                role="listbox"
                aria-label="Location suggestions"
                className={cn(
                  "absolute z-50 max-h-[40vh] overflow-y-auto rounded-lg border border-border/15 bg-popover p-4 shadow lg:max-h-[50vh]",
                  isSuggestionBgWhite
                    ? "border-pure-black/10 bg-pure-white"
                    : ""
                )}
                style={
                  isLessThanLg
                    ? {
                        width: containerRef.current?.offsetWidth,
                        left: -(inputRef?.current?.parentElement
                          ? inputRef.current.parentElement.getBoundingClientRect()
                              .left -
                            (containerRef.current?.getBoundingClientRect()
                              .left ?? 0)
                          : 0),
                        top: suggestionBoxTopOffset ?? "3.5rem",
                      }
                    : {
                        width: suggestionBoxWidth,
                        left: (suggestionBoxLeftOffset ?? 0) * -1,
                        top: suggestionBoxTopOffset ?? "3.5rem",
                      }
                }
              >
                {showRecentSearches &&
                !inputValue &&
                favorites?.enabled &&
                favoriteHotelSuggestions.length ? (
                  <Link
                    href={favoritesHref}
                    className={cn(
                      "block text-sm font-semibold text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      isSuggestionBgWhite && "text-pure-black/60"
                    )}
                    onMouseDown={(event) => event.preventDefault()}
                    onFocus={cancelBlurTimer}
                    onBlur={() => setInputFocus(false)}
                  >
                    Favorites
                  </Link>
                ) : null}
                {showRecentSearches &&
                  !inputValue &&
                  favoriteHotelSuggestions.map((suggestion) => (
                    <SuggestionItem
                      key={suggestion.placeId}
                      id={`${listId}-place-${suggestion.placeId}`}
                      active={suggestion === activeSuggestion}
                      onSuggestionClick={onSuggestionClick}
                      setActiveSuggestion={setActiveSuggestion}
                      suggestion={suggestion}
                      isSuggestionBgWhite={isSuggestionBgWhite}
                    />
                  ))}
                {showRecentSearches &&
                !inputValue &&
                favorites?.enabled &&
                favorites.history.status === "loading" &&
                !favoriteHotelSuggestions.length ? (
                  <div className="text-sm text-muted-foreground">
                    Loading favorites…
                  </div>
                ) : null}
                {showRecentSearches &&
                !inputValue &&
                favorites?.enabled &&
                favorites.history.status === "error" ? (
                  <div className="flex items-center justify-between gap-2 text-sm text-destructive">
                    <span>Couldn’t load favorites.</span>
                    <Button
                      type="button"
                      variant="link"
                      size="none"
                      onClick={() => void favorites.retryHistory()}
                    >
                      Try again
                    </Button>
                  </div>
                ) : null}
                {showRecentSearches &&
                !inputValue &&
                favorites?.enabled &&
                favorites.history.nextCursor &&
                favorites.history.status !== "loading" ? (
                  <Button
                    type="button"
                    variant="link"
                    size="none"
                    onClick={() => void favorites.loadMoreHistory()}
                  >
                    Show more favorites
                  </Button>
                ) : null}
                {showRecentSearches &&
                !inputValue &&
                recentHotelSuggestions.length ? (
                  <Link
                    href="/recently-viewed"
                    className={cn(
                      "block text-sm font-semibold text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      isSuggestionBgWhite && "text-pure-black/60"
                    )}
                    onMouseDown={(event) => event.preventDefault()}
                    onFocus={cancelBlurTimer}
                    onBlur={() => setInputFocus(false)}
                  >
                    Recently viewed hotels
                  </Link>
                ) : null}
                {showRecentSearches &&
                  !inputValue &&
                  recentHotelSuggestions.map((suggestion) => (
                    <SuggestionItem
                      key={suggestion.placeId}
                      id={`${listId}-place-${suggestion.placeId}`}
                      active={suggestion === activeSuggestion}
                      onSuggestionClick={onSuggestionClick}
                      setActiveSuggestion={setActiveSuggestion}
                      suggestion={suggestion}
                      isSuggestionBgWhite={isSuggestionBgWhite}
                    />
                  ))}
                {showRecentSearches && !inputValue && recentSearches.length ? (
                  <div
                    className={cn(
                      "text-sm font-semibold text-muted-foreground",
                      isSuggestionBgWhite && "text-pure-black/60"
                    )}
                  >
                    Recent searches
                  </div>
                ) : null}
                {showRecentSearches &&
                  !inputValue &&
                  recentSearches?.map((suggestion, index) => (
                    <SuggestionItem
                      key={index}
                      id={`${listId}-place-${suggestion.placeId}`}
                      active={suggestion === activeSuggestion}
                      onSuggestionClick={onSuggestionClick}
                      setActiveSuggestion={(value) => {
                        setActiveSupplementalKey(undefined);
                        setActiveSuggestion(value);
                      }}
                      suggestion={suggestion}
                      isSuggestionBgWhite={isSuggestionBgWhite}
                    />
                  ))}
                {!isCarSearch &&
                suggestions.hotels.length &&
                (suggestions.hotels.length < 5 ||
                  (showRecentSearches && recentActivity.length)) ? (
                  <div
                    className={cn(
                      "text-sm font-semibold text-muted-foreground",
                      isSuggestionBgWhite && "text-pure-black/60"
                    )}
                  >
                    Hotels
                  </div>
                ) : null}
                {!isCarSearch &&
                  suggestions.hotels?.map((suggestion, index) => (
                    <SuggestionItem
                      key={index}
                      id={`${listId}-place-${suggestion.placeId}`}
                      active={suggestion.placeId === activeSuggestion?.placeId}
                      onSuggestionClick={onSuggestionClick}
                      setActiveSuggestion={(value) => {
                        setActiveSupplementalKey(undefined);
                        setActiveSuggestion(value);
                      }}
                      suggestion={suggestion}
                      isSuggestionBgWhite={isSuggestionBgWhite}
                    />
                  ))}
                {suggestions.places.length &&
                (suggestions.places.length < 5 ||
                  (showRecentSearches && recentActivity.length)) ? (
                  <div
                    className={cn(
                      "text-sm font-semibold text-muted-foreground",
                      isSuggestionBgWhite && "text-pure-black/60"
                    )}
                  >
                    Locations
                  </div>
                ) : null}
                {suggestions.places?.map((suggestion, index) => (
                  <SuggestionItem
                    key={index}
                    id={`${listId}-place-${suggestion.placeId}`}
                    active={suggestion.placeId === activeSuggestion?.placeId}
                    onSuggestionClick={onSuggestionClick}
                    setActiveSuggestion={(value) => {
                      setActiveSupplementalKey(undefined);
                      setActiveSuggestion(value);
                    }}
                    suggestion={suggestion}
                    isSuggestionBgWhite={isSuggestionBgWhite}
                  />
                ))}
                {supplemental.map((row, index) => (
                  <React.Fragment key={row.key}>
                    {row.group &&
                      row.group !== supplemental[index - 1]?.group && (
                        <div
                          className={cn(
                            "mt-2 text-sm font-semibold text-muted-foreground",
                            isSuggestionBgWhite && "text-pure-black/60"
                          )}
                        >
                          {row.group}
                        </div>
                      )}
                    <Button
                      type="button"
                      id={`${listId}-extra-${row.key}`}
                      ref={(element) => {
                        if (row.key === activeSupplementalKey)
                          element?.scrollIntoView?.({ block: "nearest" });
                      }}
                      role="option"
                      aria-selected={row.key === activeSupplementalKey}
                      variant="ghost"
                      className={cn(
                        "h-auto w-full flex-col items-start whitespace-normal p-4 text-left text-popover-foreground",
                        row.key === activeSupplementalKey &&
                          (isSuggestionBgWhite
                            ? "bg-pure-black/5"
                            : "bg-accent"),
                        isSuggestionBgWhite &&
                          "text-pure-black hover:bg-pure-black/5 hover:text-pure-black"
                      )}
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseEnter={() => {
                        setActiveSuggestion(undefined);
                        setActiveSupplementalKey(row.key);
                      }}
                      onClick={() => selectSupplemental(row)}
                    >
                      <span className="text-sm font-light">
                        {row.description}
                      </span>
                      {row.secondaryText && (
                        <span
                          className={cn(
                            "text-xs font-light text-muted-foreground",
                            isSuggestionBgWhite && "text-pure-black/60"
                          )}
                        >
                          {row.secondaryText}
                        </span>
                      )}
                    </Button>
                  </React.Fragment>
                ))}
              </div>
            )
          }
          {...otherProps}
        />
        {onClear && hasValue && (
          <Button
            type="button"
            variant="link"
            size="none"
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground lg:-right-2"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              if (inputRef?.current) {
                inputRef.current.value = "";
                inputRef.current.focus();
              }
              invalidateSupplemental();
              setInputValue("");
              // Reset the query and invalidate any in-flight response so a
              // stale prediction can't repopulate the dropdown after clear.
              setAutocompleteQuery("");
              requestIdRef.current++;
              setHasValue(false);
              _onSuggestionClick?.(null);
              onClear();
            }}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    );
  }
);

type SuggestionItemProps = {
  id?: string;
  suggestion: SearchSuggestion;
  active: boolean;
  onSuggestionClick: (suggestion: SearchSuggestion) => void;
  setActiveSuggestion: (suggestion: SearchSuggestion) => void;
  isSuggestionBgWhite?: boolean;
};

const SuggestionItem: React.FC<SuggestionItemProps> = ({
  id,
  suggestion,
  active,
  onSuggestionClick,
  setActiveSuggestion,
  isSuggestionBgWhite,
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (active) itemRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [active]);
  return (
    <div
      ref={itemRef}
      id={id}
      data-testid="suggestion-item"
      role="option"
      aria-selected={active}
      className={cn(
        "cursor-pointer p-4",
        active
          ? isSuggestionBgWhite
            ? "rounded-md bg-pure-black/5"
            : "rounded-md bg-accent"
          : "",
        suggestion.timeRangeString
          ? "flex flex-col justify-between md:flex-row md:items-center"
          : ""
      )}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onMouseUp={(e) => {
        e.preventDefault();
        e.stopPropagation();
        itemRef.current?.click();
      }}
      onClick={() => {
        onSuggestionClick(suggestion);
      }}
      onMouseEnter={() => setActiveSuggestion(suggestion)}
    >
      <span
        className={cn(
          "text-sm font-light text-primary",
          isSuggestionBgWhite ? "text-pure-black" : ""
        )}
      >
        {suggestion.description}
      </span>
      {suggestion.timeRangeString && (
        <span
          className={cn(
            "text-xs font-light text-muted-foreground",
            isSuggestionBgWhite ? "text-pure-black/80" : ""
          )}
        >
          {suggestion.timeRangeString}
        </span>
      )}
    </div>
  );
};

SearchInput.displayName = "SearchInput";

export default SearchInput;
